/**
 * What of an AI thread is safe to persist at a given instant (22.08.2026).
 *
 * Between turns this is trivial: the rendered `messages` and the model-side
 * history as they are. Mid-turn it is not — the transcript ends in a bubble
 * still marked `isStreaming`, and the model-side history ref is only updated
 * when a turn SETTLES, so it lacks the user turn that is in flight.
 *
 * Closing the panel mid-reply used to lose all of that. The rule for what to
 * keep is the one AIChatPanel's abort and error branches already apply:
 * persist the in-flight history (the user turn plus any tool rounds that DID
 * complete), fold streamed text in as an assistant entry when there is some,
 * and never persist a bare tool_use block — an assistant turn ending in an
 * unanswered tool_use breaks the next API call.
 *
 * A tool card caught mid-flight — waiting on Confirm/Cancel, or executing —
 * is persisted as cancelled: the gate and the spinner belonged to an instance
 * that no longer exists, and a reopened panel must not show an enabled Confirm
 * button that does nothing, or a spinner that never stops.
 *
 * Pure, so the shape is testable without mounting a 2,400-line component.
 *
 * @param {object} args
 * @param {Array} args.messages            rendered transcript
 * @param {boolean} args.isStreaming       a turn is in flight
 * @param {Array} args.history             anthropicHistoryRef.current — settled turns
 * @param {Array|null} args.inFlightHistory the in-flight turn's message list (null between turns)
 * @returns {{messages: Array, anthropicHistory: Array}}
 */
function settleToolCall(tc) {
  const pending = tc?.result == null || tc.result?.__awaitingConfirm;
  return pending ? { ...tc, result: { cancelled: true }, awaitingConfirm: false } : tc;
}

export function buildPersistSnapshot({ messages, isStreaming, history, inFlightHistory }) {
  const list = Array.isArray(messages) ? messages : [];
  const settled = Array.isArray(history) ? history : [];
  if (!isStreaming) return { messages: list, anthropicHistory: settled };

  const last = list[list.length - 1];
  let finalMessages = list;
  let partialText = '';
  if (last?.role === 'assistant' && last.isStreaming) {
    partialText = last.content || '';
    finalMessages = !partialText && !(last.toolCalls || []).length
      ? list.slice(0, -1)
      : [...list.slice(0, -1), { ...last, isStreaming: false, toolCalls: (last.toolCalls || []).map(settleToolCall) }];
  }

  const base = Array.isArray(inFlightHistory) ? inFlightHistory : settled;
  return {
    messages: finalMessages,
    anthropicHistory: partialText
      ? [...base, { role: 'assistant', content: partialText }]
      : base,
  };
}
